import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import { Mission, MissionComment } from '../../types';
import clsx from 'clsx';

interface CommunicationsPanelProps {
  mission: Mission;
  onAddComment: (text: string, options?: { isCritical?: boolean }) => void;
  height?: string;
}

const CommunicationsPanel: React.FC<CommunicationsPanelProps> = ({
  mission,
  onAddComment,
  height = 'calc(100vh - 300px)',
}) => {
  const [newComment, setNewComment] = useState('');
  const [isCritical, setIsCritical] = useState(false);
  const [highlightedComments, setHighlightedComments] = useState<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const commentsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    commentInputRef.current?.focus();
  }, []);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      await onAddComment(newComment.trim(), { isCritical });
      setNewComment('');
      setIsCritical(false);
      setTimeout(() => commentInputRef.current?.focus(), 100);
    }
  };

  const sortedComments = [...(mission.comments || [])].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  useEffect(() => {
    if (sortedComments.length > 0 && commentsListRef.current) {
      commentsListRef.current.scrollTop = 0;
    }
    if (commentInputRef.current && !panelRef.current?.contains(document.activeElement)) {
      commentInputRef.current.focus();
    }
  }, [sortedComments.length]);

  useEffect(() => {
    const latest = sortedComments[0];
    if (latest && !highlightedComments.has(latest.id)) {
      setHighlightedComments(prev => new Set([...prev, latest.id]));
      setTimeout(() => {
        setHighlightedComments(prev => {
          const next = new Set(prev);
          next.delete(latest.id);
          return next;
        });
      }, 3000);
    }
  }, [sortedComments]);

  const getCommentClass = (comment: MissionComment) => {
    const isHighlighted = highlightedComments.has(comment.id);
    const c = comment as any;

    if (c.isCritical) {
      return clsx('border-l-4 border-red-500 bg-red-50 p-3 rounded-r-lg', isHighlighted && 'animate-pulse bg-red-100');
    }
    if (c.isSystemEvent) {
      return clsx('border-l-4 border-blue-500 bg-blue-50 p-3 rounded-r-lg italic', isHighlighted && 'animate-pulse bg-blue-100');
    }
    return clsx('bg-gray-50 p-3 rounded-lg', isHighlighted && 'animate-pulse bg-yellow-50');
  };

  return (
    <div ref={panelRef} data-testid="comms-panel" className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
          <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
          Communications Log
          {sortedComments.some((c: any) => c.isCritical && !c.acknowledged) && (
            <BellSolidIcon className="h-4 w-4 ml-2 text-red-600 animate-bounce" />
          )}
        </h3>
      </div>

      {/* Comment input */}
      <div className="border-b border-gray-200 p-4 bg-gray-50">
        <form onSubmit={handleSubmitComment}>
          <div className="flex gap-2">
            <div className="flex-1">
              <div className="relative">
                <input
                  ref={commentInputRef}
                  data-testid="comms-panel-comment-input"
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className={clsx(
                    'form-input w-full pr-10',
                    isCritical && 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  )}
                  placeholder="Type a comment..."
                  maxLength={500}
                />
                <button
                  type="button"
                  onClick={() => setIsCritical(!isCritical)}
                  className={clsx(
                    'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors',
                    isCritical ? 'text-red-600 hover:bg-red-100' : 'text-gray-400 hover:bg-gray-100'
                  )}
                  title="Mark as critical"
                >
                  <ExclamationTriangleIcon className="h-5 w-5" />
                </button>
              </div>
              {isCritical && (
                <p className="text-xs text-red-600 mt-1">This will be marked as a critical update</p>
              )}
            </div>
            <button type="submit" className="btn btn-primary" disabled={!newComment.trim()}>
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </form>
      </div>

      {/* Comments list */}
      <div ref={commentsListRef} className="flex-1 overflow-y-auto p-4 space-y-2" style={{ maxHeight: height }}>
        {sortedComments.length > 0 ? (
          sortedComments.map((comment) => {
            const c = comment as any;
            return (
              <div key={comment.id} className={getCommentClass(comment)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {c.isCritical && <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />}
                      {c.isSystemEvent && <SparklesIcon className="h-4 w-4 text-blue-600" />}
                      <span className="text-xs font-mono text-gray-600">
                        {format(new Date(comment.timestamp), 'HH:mm:ss')}
                      </span>
                      <span className={clsx('text-xs font-semibold', c.isCritical ? 'text-red-700' : 'text-blue-700')}>
                        ({comment.dispatcherInitials || comment.dispatcher?.preferredInitials})
                      </span>
                      {c.isSystemEvent && <span className="text-xs text-gray-500 italic">System</span>}
                    </div>
                    <p className={clsx('text-sm break-words', c.isCritical ? 'text-red-900 font-medium' : 'text-gray-900')}>
                      {comment.commentText}
                    </p>
                  </div>
                  {c.isCritical && !c.acknowledged && (
                    <button className="ml-2 text-red-600 hover:text-red-800" title="Acknowledge critical message">
                      <CheckCircleIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <ChatBubbleLeftRightIcon className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No communications yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationsPanel;
